import { lazy, Suspense } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "@/sections/Header";
import Footer from "@/sections/Footer";
import TipCoin from "@/sections/TipCoin";
import ErrorBoundary from "@/components/ErrorBoundary";
import LaunchKit from "@/sections/LaunchKit";
import CustomizePanel from "@/components/CustomizePanel";
import LaunchJourney from "@/components/LaunchJourney";
import JupiterTracker from "@/components/JupiterTracker";
import CoinChart from "@/sections/CoinChart";
import PromoLadder from "@/sections/PromoLadder";
import CoinHeader from "@/sections/CoinHeader";
import RiskDisclaimer from "@/sections/RiskDisclaimer";
import CoinWhitepaper from "@/sections/CoinWhitepaper";
import ShareTools from "@/sections/ShareTools";
import WhaleAlert from "@/components/WhaleAlert";
import TrustPanel from "@/components/trust/TrustPanel";
import BackerWall from "@/components/BackerWall";
import useCoinDetail from "@/hooks/useCoinDetail";

// Lazy: keeps heavy Solana mint libs (spl-token/metaplex) OUT of the main bundle
// so an old browser that can't parse them still renders the whole site.
const MintPanel = lazy(() => import("@/components/MintPanel"));
const LiquidityPanel = lazy(() => import("@/components/LiquidityPanel"));
const LpLiveCard = lazy(() => import("@/components/lp/LpLiveCard"));

const NotFound = () => (
  <div className="min-h-screen bg-[#050505] text-white">
    <Header />
    <main className="pt-24 pb-20 max-w-3xl mx-auto px-6 md:px-10 text-center" data-testid="coin-not-found">
      <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#ff3333] mb-3">// 404</div>
      <h1 className="font-display text-5xl font-black uppercase tracking-tighter mb-4">Coin not found.</h1>
      <p className="text-[#e2e2e2] mb-8">Maybe it's still in draft. Or never existed.</p>
      <Link
        to="/create"
        className="btn-brutal inline-flex bg-[#cfff04] text-[#050505] font-bold uppercase tracking-wider text-sm px-6 py-3"
      >
        Launch yours →
      </Link>
    </main>
    <Footer />
  </div>
);

const LoadingSkeleton = () => (
  <div className="min-h-screen bg-[#050505] text-white">
    <Header />
    <main className="pt-24 pb-20 max-w-3xl mx-auto px-6 md:px-10">
      <div className="border border-[#3a3a3a] bg-[#121212] h-96 animate-pulse" />
    </main>
  </div>
);

export default function CoinDetail() {
  const { slug } = useParams();
  const { coin, price, chart, likes, liked, notFound, load, like, share } = useCoinDetail(slug);

  if (notFound) return <NotFound />;
  if (!coin) return <LoadingSkeleton />;

  const wp = coin.whitepaper || {};
  const isPublished = coin.status === "published" || coin.status === "featured";

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <main className="pt-24 pb-20" data-testid="coin-page">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          {isPublished && <WhaleAlert slug={coin.slug} />}
          <CoinHeader coin={coin} wp={wp} likes={likes} liked={liked} onLike={like} onShare={share} />

          <CoinChart coin={coin} price={price} chart={chart} />

          {(isPublished || coin.is_owner) && <TrustPanel coin={coin} onChanged={load} />}

          <CoinWhitepaper wp={wp} />

          {/* Viral share tools — public */}
          {isPublished && <ShareTools coin={coin} />}

          {/* On-chain mint panel — creator only, unlocked by publishing */}
          {isPublished && coin.is_owner && (
            <ErrorBoundary
              fallback={
                <div className="border border-[#3a3a3a] bg-[#121212] p-6 mb-6 text-sm text-[#e2e2e2]" data-testid="mint-panel-unsupported">
                  // The mint panel couldn't load on this browser. Use an up-to-date desktop
                  Chrome, Brave, or Firefox with the Phantom extension to mint.
                </div>
              }
            >
              <Suspense fallback={<div className="border border-[#3a3a3a] bg-[#121212] h-48 animate-pulse mb-6" data-testid="mint-panel-loading" />}>
                <MintPanel coin={coin} onMinted={load} />
              </Suspense>
            </ErrorBoundary>
          )}

          {/* One-click Raydium liquidity — creator manages; public sees trade links */}
          {isPublished && coin.is_owner && (coin.mint_address || coin.devnet_mint_address) && (
            <ErrorBoundary fallback={null}>
              <Suspense fallback={<div className="border border-[#3a3a3a] bg-[#121212] h-32 animate-pulse mb-6" data-testid="lp-panel-loading" />}>
                <LiquidityPanel coin={coin} onCreated={load} />
              </Suspense>
            </ErrorBoundary>
          )}
          {isPublished && !coin.is_owner && coin.lp_pool_id && (
            <ErrorBoundary fallback={null}>
              <Suspense fallback={null}>
                <div className="mb-6"><LpLiveCard coin={coin} /></div>
              </Suspense>
            </ErrorBoundary>
          )}

          {/* Owner mission control — journey + Jupiter progress */}
          {coin.is_owner && <LaunchJourney coin={coin} />}
          {coin.is_owner && <JupiterTracker coin={coin} />}

          {/* AI Launch Kit upsell / viewer — creator only */}
          {isPublished && coin.is_owner && <LaunchKit coin={coin} />}

          {/* Impulse customization — creator only */}
          {isPublished && coin.is_owner && <CustomizePanel coin={coin} onUpdated={load} />}

          {/* Tipping panel */}
          <TipCoin coin={coin} />

          {/* Backer wall — social proof */}
          {isPublished && <BackerWall coin={coin} />}

          {/* Promotion tier ladder */}
          {isPublished && <PromoLadder coin={coin} />}

          <RiskDisclaimer className="mb-6" />

          <div className="text-center pt-8 border-t border-[#3a3a3a]">
            <Link to="/explore" className="font-mono text-xs uppercase tracking-widest text-[#d0d0d0] hover:text-[#cfff04]" data-testid="back-to-explore">
              ← Back to explore
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
